import React from "react";
import { twMerge } from "tailwind-merge";

/**
 * Typography component for consistent text styling.
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.variant='paragraph'] - Typography variant (e.g., 'h1', 'text-lg', 'lead')
 *   Valid variants include: (size/weight)
 *   - 'h1': Heading level 1 (24px/600)
 *   - 'h2': Heading level 2 (24px/600)
 *   - 'h3': Heading level 3 (20px/600)
 *   - 'h4': Heading level 4 (18px/600)
 *   - 'h5': Heading level 5 (16px/600)
 *   - 'h6': Heading level 6 (14px/600)
 *   - 'lead': Lead paragraph style (18px/600)
 *   - 'large': Large text style (18px/300)
 *   - 'normal': Normal text style (16px/300)
 *   - 'small': Small text style (14px/300)
 *   - 'especially': Especially small text style (12px/300)
 *   - 'anotation': Annotation text style (10px/300)
 * @param {string} [props.color='inherit'] - Text color
 * @param {string} [props.as=null] - HTML element type (overrides variant)
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {ReactNode} props.children - Content to be rendered inside Typography
 * @param {React.Ref} ref - Forwarded ref
 * @param {Object} rest - Additional props to be spread on the rendered component
 * @returns {React.Element} Rendered Typography component
 */
const Typography = React.forwardRef(({
    variant = 'text-normal',
    color = 'inherit',
    as = null,
    className = '',
    children,
    ...rest
}, ref) => {
    const VARIANTS_TEMPLATE = {
        h1: {
            component: 'h1',
            style: 'text-2xl font-semibold'
        },
        h2: {
            component: 'h2',
            style: 'text-2xl font-semibold'
        },
        h3: {
            component: 'h3',
            style: 'text-xl font-semibold'
        },
        h4: {
            component: 'h4',
            style: 'text-lg font-semibold'
        },
        h5: {
            component: 'h5',
            style: 'text-base font-semibold'
        },
        h6: {
            component: 'h6',
            style: 'text-sm font-semibold'
        },
        lead: {
            component: 'p',
            style: 'text-lg font-semibold'
        },
        large: {
            component: 'p',
            style: 'text-lg font-light'
        },
        normal: {
            component: 'p',
            style: 'text-base font-light'
        },
        small: {
            component: 'p',
            style: 'text-sm font-light'
        },
        especially: {
            component: 'p',
            style: 'text-xs font-light'
        },
        anotation: {
            component: 'p',
            style: 'text-2xs font-light'
        }
    };

    var template;
    if (variant.toLowerCase() in VARIANTS_TEMPLATE) {
        template = VARIANTS_TEMPLATE[variant];
    } else {
        // default template component
        template = VARIANTS_TEMPLATE['normal'];
    }

    var component = as || template.component;
    var styles = template.style;

    return React.createElement(
        component,
        {
            ...rest,
            ref,
            className: twMerge(`text-${color} ${styles} ${className}`),
        },
        children
    );
});

export default Typography;

